import { Controller, Application } from "@hotwired/stimulus";
import Autonumeric from "autonumeric";
import getCsrfToken from "../utils/csrf";
import $ from "jquery";

export default class extends Controller {
  static targets = [
    "rentPerDay",
    "rentPerWeek",
    "rentPerMonth",
    "option",
    "instantBooking",
    "enableSendMessage",
    "instantBookingTrue",
    "instantBookingFalse",
    "notificationCloseIcon",
    "publish",
    "errorBlock",
    "offlineBookable",
    "disableModal",
  ];

  static values = {
    enableUrl: String,
    whitelabelFeatured: Boolean,
    updateUrl: String,
    photoError: String,
    publishMessage: String,
  };

  connect() {
    this.pendingEnableToggle = null;
    this.boundDisableModalHiddenHandler = () => {
      this.pendingEnableToggle = null;
    };

    if (this.hasDisableModalTarget) {
      $(this.disableModalTarget).on("hidden.bs.modal", this.boundDisableModalHiddenHandler);
    }

    if (this.hasRentPerDayTarget) {
      this.fillPrices(this.rentPerDayTarget);
    }
    if (this.hasRentPerWeekTarget) {
      this.fillPrices(this.rentPerWeekTarget);
    }
    if (this.hasRentPerMonthTarget) {
      this.fillPrices(this.rentPerMonthTarget);
    }
  }

  disconnect() {
    if (this.hasDisableModalTarget && this.boundDisableModalHiddenHandler) {
      $(this.disableModalTarget).off("hidden.bs.modal", this.boundDisableModalHiddenHandler);
    }
  }

  handlePublishConfirmation(event) {
    if (confirm(this.publishMessageValue)) {
      event.preventDefault();
      this.publishTarget.click();
    }
  }

  handleFetch(url, method, body = {}) {
    fetch(url, {
      method: method,
      headers: {
        "Content-Type": "application/json",
        Accept: "text/html",
        "X-CSRF-Token": getCsrfToken(),
      },
      body: JSON.stringify(body),
    })
      .then((response) => {
        const redirectTo = response.headers.get("redirect-to");
        if (redirectTo) {
          window.location.href = redirectTo;
          return;
        }

        if ([400, 500].includes(response.status)) return;

        const renderTarget = response.headers.get("render-target");

        return response.text().then((html) => {
          Turbolinks.clearCache();

          if (renderTarget) {
            const dstEl = document.querySelector(renderTarget);
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = html;

            const srcEl = tempDiv.querySelector(renderTarget);

            if (srcEl && dstEl) {
              dstEl.innerHTML = srcEl.innerHTML;
            }
          } else {
            document.body.innerHTML = html;
          }

          Turbolinks.dispatch("turbolinks:load");
        });
      })
      .catch((error) => {
        console.error("Fetch failed", error);
      });
  }

  publish(event) {
    event.preventDefault();
    this.handleFetch(this.updateUrlValue, "PUT", {
      render_target: "#listing_edit",
      v2_edit: true,
      listing: { current_partial: "listing_publish" },
    });
  }

  enable(event) {
    const checkbox = event?.currentTarget;
    if (!checkbox) return;

    const isDeactivating = checkbox.checked === false;
    const isIncludedInBundle = checkbox.dataset.includedInBundle === "true";

    if (isDeactivating && isIncludedInBundle && this.hasDisableModalTarget) {
      checkbox.checked = true;
      this.pendingEnableToggle = checkbox;
      $(this.disableModalTarget).modal("show");
      return;
    }

    if (isDeactivating) {
      const confirmMessage = checkbox.dataset.disableConfirmMessage;
      if (isDeactivating && confirmMessage && !confirm(confirmMessage)) {
        checkbox.checked = true;
        return;
      }
    }

    this.submitEnableToggle();
  }

  confirmDisableListing(event) {
    event.preventDefault();

    if (this.pendingEnableToggle) {
      this.pendingEnableToggle.checked = false;
    }

    if (this.hasDisableModalTarget) {
      $(this.disableModalTarget).modal("hide");
    }

    this.pendingEnableToggle = null;
    this.submitEnableToggle();
  }

  submitEnableToggle() {
    this.handleFetch(this.enableUrlValue, "PATCH", {
      render_target: "#listing_edit",
      v2_edit: true,
    });
  }

  propertyChanged(event) {
    this.handleFetch(this.updateUrlValue, "PUT", {
      render_target: "#modal__location",
      v2_edit: true,
      listing: { current_partial: "listing_property", building_id: event.target.value },
    });
  }

  set_whitelabel_featured() {
    this.handleFetch(this.updateUrlValue, "PUT", {
      render_target: "#listing_edit",
      v2_edit: true,
      listing: {
        current_partial: "whitelabel_featured",
        whitelabel_featured: !this.whitelabelFeaturedValue,
      },
    });
  }

  onSubmitPhotosForm(event) {
    this.checkFiles(event);
  }

  checkFiles(event) {
    const files = document.querySelector(".files .template-download");
    if (!files) {
      alert(this.photoErrorValue);
      event.preventDefault();
    }
  }

  optionChanged(event) {
    this.fillPrices(event.target);
  }

  handleNotificationClose() {
    this.notificationCloseIconTarget.style.display = "none";
  }

  advanceTypeChanged(event) {
    const selectedOption = this.optionTargets.find((radio) => radio.checked);
    const isShowError = event.params.showAdvanceTypeToggleError;

    const enableSendMessageYes = this.enableSendMessageTarget.querySelector('input[value="true"]');
    const enableSendMessageNo = this.enableSendMessageTarget.querySelector('input[value="false"]');

    if (enableSendMessageYes) {
      enableSendMessageYes.checked = false;
      enableSendMessageYes.closest("label")?.classList.remove("active");
    }
    if (enableSendMessageNo) {
      enableSendMessageNo.checked = true;
      enableSendMessageNo.closest("label")?.classList.add("active");
    }

    if (this.errorBlockTarget) {
      if (isShowError && this.errorBlockTarget.classList.contains("hidden")) {
        this.errorBlockTarget.classList.remove("hidden");
      }

      if (!isShowError && !this.errorBlockTarget.classList.contains("hidden")) {
        this.errorBlockTarget.classList.add("hidden");
      }
    }

    if (selectedOption && selectedOption.value === "no_advance") {
      this.offlineBookableTarget.value = true;

      if (!this.instantBookingTarget.classList.contains("!hidden")) {
        this.instantBookingTrueTarget.checked = false;
        this.instantBookingFalseTarget.checked = true;
        this.instantBookingTarget.classList.add("!hidden");
      }

      if (!this.enableSendMessageTarget.classList.contains("!hidden")) {
        this.enableSendMessageTarget.classList.add("!hidden");
      }
    } else {
      this.offlineBookableTarget.value = false;
      this.instantBookingTrueTarget.checked = true;
      this.instantBookingFalseTarget.checked = false;
      this.instantBookingTarget.classList.remove("!hidden");
      this.enableSendMessageTarget.classList.remove("!hidden");
    }
  }

  optionIntegerChanged(event) {
    let value = event.target.value;
    value = value.replace(/\D/g, "");
    let intValue = parseInt(value, 10);

    const allowZero = event.params.optionIntegerChangedAllowZero === true;

    if (isNaN(intValue) || (allowZero ? intValue < 0 : intValue < 1)) {
      event.target.value = "";
    } else {
      event.target.value = intValue;
    }
  }

  textOnlyChanged(event) {
    let value = event.target.value;

    // Keep only letters and spaces
    value = value.replace(/[^\p{L} ]/gu, "");

    // Replace multiple consecutive spaces with a single space
    value = value.replace(/\s{2,}/g, " ");

    // trim leading/trailing spaces
    value = value.trimStart();

    event.target.value = value;
  }

  fillPrices(target) {
    const pricesSection = document.getElementById("pricing_section");
    const decimalMark = pricesSection.dataset.a_dec;
    const thousandsSeparator = pricesSection.dataset.a_sep;

    const oldInput = target;
    const input = document.createElement("input");
    input.className = oldInput.className;
    input.classList.remove("input__danger");

    oldInput.parentNode.replaceChild(input, oldInput);

    input.id = oldInput.id;
    input.setAttribute("type", "text");
    input.setAttribute("name", oldInput.name);

    const PriceObj = new Autonumeric(input, {
      digitGroupSeparator: thousandsSeparator,
      decimalCharacter: decimalMark,
      minimumValue: 0,
      maximumValue: 2147483647,
    });

    const price = target.value || "";
    input.value = price;
    PriceObj.set(price);
  }
}
